import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { sendAppointmentEmail } from '../../../services/emailService';

const AppointmentBooking = () => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [appointmentType, setAppointmentType] = useState('');
  const [visitorCount, setVisitorCount] = useState('');
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [specialRequests, setSpecialRequests] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBooked, setIsBooked] = useState(false);

  // Generate available dates (next 30 days, excluding Sundays)
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date?.setDate(today?.getDate() + i);
      
      // Skip Sundays (0 = Sunday)
      if (date?.getDay() !== 0) {
        const dateString = date?.toISOString()?.split('T')?.[0];
        const displayDate = date?.toLocaleDateString('en-IN', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        });
        
        dates?.push({
          value: dateString,
          label: displayDate
        });
      }
    }
    
    return dates;
  };

  const availableDates = generateAvailableDates();

  const timeSlots = [
    { value: '10:00', label: '10:00 AM - 11:00 AM' },
    { value: '11:00', label: '11:00 AM - 12:00 PM' },
    { value: '12:00', label: '12:00 PM - 1:00 PM' },
    { value: '14:00', label: '2:00 PM - 3:00 PM' },
    { value: '15:00', label: '3:00 PM - 4:00 PM' },
    { value: '16:00', label: '4:00 PM - 5:00 PM' },
    { value: '17:00', label: '5:00 PM - 6:00 PM' }
  ];

  const appointmentTypes = [
    { value: 'factory-tour', label: 'Factory Tour & Capability Showcase' },
    { value: 'consultation', label: 'Technical Consultation Meeting' },
    { value: 'sample-review', label: 'Sample Review & Quality Discussion' },
    { value: 'partnership', label: 'Business Partnership Discussion' },
    { value: 'project-planning', label: 'Project Planning & Timeline Discussion' }
  ];

  const visitorCounts = [
    { value: '1', label: '1 person' },
    { value: '2', label: '2 people' },
    { value: '3-5', label: '3-5 people' },
    { value: '6-10', label: '6-10 people' },
    { value: '10+', label: 'More than 10 people' }
  ];

  const handleContactInfoChange = (field, value) => {
    setContactInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);

    try {
      // Format date and time
      const formattedDate = new Date(selectedDate)?.toLocaleDateString('en-IN', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      const timeSlotLabel = timeSlots?.find(slot => slot?.value === selectedTime)?.label;
      const appointmentTypeLabel = appointmentTypes?.find(type => type?.value === appointmentType)?.label;
      const visitorCountLabel = visitorCounts?.find(count => count?.value === visitorCount)?.label;
      
      // Send email notification
      const emailResult = await sendAppointmentEmail({
        name: contactInfo?.name,
        email: contactInfo?.email,
        phone: contactInfo?.phone,
        company: contactInfo?.company,
        date: formattedDate,
        time: timeSlotLabel,
        type: appointmentTypeLabel,
        visitorCount: visitorCountLabel,
        specialRequests: specialRequests
      });
      
      // Format message for WhatsApp
      const whatsappMessage = `*Factory Visit Appointment Request*%0A%0A` +
        `*Date:* ${formattedDate}%0A` +
        `*Time:* ${timeSlotLabel}%0A` +
        `*Type:* ${appointmentTypeLabel}%0A` +
        `*Visitors:* ${visitorCountLabel}%0A%0A` +
        `*Contact Details:*%0A` +
        `*Name:* ${contactInfo?.name}%0A` +
        `*Email:* ${contactInfo?.email}%0A` +
        `*Phone:* ${contactInfo?.phone}%0A` +
        `*Company:* ${contactInfo?.company || 'Not provided'}%0A%0A` +
        `*Special Requests:*%0A${specialRequests || 'None'}`;
      
      // Send to WhatsApp
      const whatsappNumber = '919974056700'; // +91 99740 56700
      window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
      
      // Show success message
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsBooked(true);
      
      // Show email status
      if (!emailResult?.success) {
        console.warn('Email notification may not have been sent. Please check your configuration.');
      }
    } catch (error) {
      console.error('Booking error:', error);
      alert('There was an error processing your request. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isBooked) {
    return (
      <section id="appointment-section" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-success/10 mb-6">
              <Icon name="CalendarCheck" size={48} className="text-success" />
            </div>
            
            <h2 className="text-3xl font-poppins font-bold text-text-primary mb-4">
              Appointment Confirmed!
            </h2>
            
            <div className="bg-surface rounded-lg p-6 mb-8 max-w-2xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                <div>
                  <span className="text-sm font-medium text-text-secondary">Date & Time</span>
                  <p className="font-semibold text-text-primary">
                    {new Date(selectedDate)?.toLocaleDateString('en-IN', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                  <p className="text-primary font-medium">
                    {timeSlots?.find(slot => slot?.value === selectedTime)?.label}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-text-secondary">Appointment Type</span>
                  <p className="font-semibold text-text-primary">
                    {appointmentTypes?.find(type => type?.value === appointmentType)?.label}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center justify-center space-x-2 text-success">
                <Icon name="Mail" size={20} />
                <span>Confirmation email sent to {contactInfo?.email}</span>
              </div>
              <div className="flex items-center justify-center space-x-2 text-primary">
                <Icon name="Phone" size={20} />
                <span>We'll call you at {contactInfo?.phone} to confirm details</span>
              </div>
            </div>
            
            <div className="bg-gradient-heritage rounded-lg p-6 text-white mb-8">
              <h3 className="text-xl font-semibold mb-4">What to Expect</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-start space-x-2">
                  <Icon name="MapPin" size={16} className="text-golden mt-0.5 flex-shrink-0" />
                  <span>Meet at main reception, Plot No. 45, Naroda GIDC</span>
                </div>
                <div className="flex items-start space-x-2">
                  <Icon name="Clock" size={16} className="text-golden mt-0.5 flex-shrink-0" />
                  <span>Please arrive 10 minutes early</span>
                </div>
                <div className="flex items-start space-x-2">
                  <Icon name="Shield" size={16} className="text-golden mt-0.5 flex-shrink-0" />
                  <span>Safety equipment will be provided</span>
                </div>
                <div className="flex items-start space-x-2">
                  <Icon name="FileText" size={16} className="text-golden mt-0.5 flex-shrink-0" />
                  <span>Bring valid ID for security clearance</span>
                </div>
              </div>
            </div>
            
            <Button
              variant="outline"
              onClick={() => setIsBooked(false)}
              iconName="Calendar"
              iconPosition="left"
            >
              Book Another Appointment
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="appointment-section" className="py-8 sm:py-12 lg:py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-6 sm:mb-8 lg:mb-12">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-poppins font-bold text-text-primary mb-2 sm:mb-3 lg:mb-4">
            Schedule Your Factory Visit
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-text-secondary max-w-2xl mx-auto px-2">
            Book a personalized tour of our manufacturing facility and see our fabric production and quality standards in action.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6 lg:space-y-8">
          {/* Appointment Details */}
          <div className="card-elevated p-4 sm:p-5 lg:p-6">
            <h3 className="text-base sm:text-lg lg:text-xl font-poppins font-semibold text-text-primary mb-4 sm:mb-5 lg:mb-6 flex items-center">
              <Icon name="Calendar" size={20} className="text-primary mr-2 sm:w-6 sm:h-6" />
              Appointment Details
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label="Preferred Date"
                placeholder="Select a date"
                options={availableDates}
                value={selectedDate}
                onChange={setSelectedDate}
                required
                searchable
              />

              <Select
                label="Preferred Time"
                placeholder="Select time slot"
                options={timeSlots}
                value={selectedTime}
                onChange={setSelectedTime}
                required
                disabled={!selectedDate}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <Select
                label="Appointment Type"
                placeholder="What would you like to focus on?"
                options={appointmentTypes}
                value={appointmentType}
                onChange={setAppointmentType}
                required
              />

              <Select
                label="Number of Visitors"
                placeholder="How many people will visit?"
                options={visitorCounts}
                value={visitorCount}
                onChange={setVisitorCount}
                required
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="card-elevated p-6">
            <h3 className="text-xl font-poppins font-semibold text-text-primary mb-6 flex items-center">
              <Icon name="User" size={24} className="text-secondary mr-2" />
              Contact Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Full Name"
                type="text"
                placeholder="Enter your full name"
                value={contactInfo?.name}
                onChange={(e) => handleContactInfoChange('name', e?.target?.value)}
                required
              />

              <Input
                label="Email Address"
                type="email"
                placeholder="your.email@company.com"
                value={contactInfo?.email}
                onChange={(e) => handleContactInfoChange('email', e?.target?.value)}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <Input
                label="Phone Number"
                type="tel"
                placeholder="+91 00000 00000"
                value={contactInfo?.phone}
                onChange={(e) => handleContactInfoChange('phone', e?.target?.value)}
                required
              />

              <Input
                label="Company Name"
                type="text"
                placeholder="Your company name"
                value={contactInfo?.company}
                onChange={(e) => handleContactInfoChange('company', e?.target?.value)}
              />
            </div>
          </div>

          {/* Special Requests */}
          <div className="card-elevated p-6">
            <h3 className="text-xl font-poppins font-semibold text-text-primary mb-6 flex items-center">
              <Icon name="MessageSquare" size={24} className="text-textile mr-2" />
              Special Requests or Questions
            </h3>
            
            <textarea
              className="w-full h-32 resize-none rounded-md border border-gray-200 bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 transition-colors"
              placeholder="Any specific areas you'd like to focus on, questions about our processes, or special requirements for your visit..."
              value={specialRequests}
              onChange={(e) => setSpecialRequests(e?.target?.value)}
            />
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <Button
              type="submit"
              variant="default"
              size="lg"
              loading={isSubmitting}
              disabled={!selectedDate || !selectedTime || !appointmentType || !contactInfo?.name || !contactInfo?.email || !contactInfo?.phone}
              iconName="Calendar"
              iconPosition="right"
              className="btn-textile golden-accent"
            >
              {isSubmitting ? 'Booking Appointment...' : 'Book Factory Visit'}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default AppointmentBooking;